import { useEffect, useMemo, useRef, useState } from "react";

import "../styles/combobox.css";

/**
 * Durchsuchbare Auswahl — tippen filtert, die Liste schlägt vor.
 *
 * Kontor hatte bis hierher nur `<datalist>`. Die filtert im Browser auf den
 * **ganzen Optionstext**: wer bei einem Projekt „Thiele" tippt, trifft das
 * Label „Thiele Weg Zaun · Familie Thiele" nur, wenn er es von vorn
 * ausschreibt. Getroffen wurde deshalb selten etwas, die ID blieb leer, und
 * das Speichern wurde mit „bitte aus der Trefferliste wählen" abgewiesen —
 * ein Feld, das aussieht wie eine Auswahl und sich benimmt wie ein Rätsel.
 * Die UI-Konventionen verlangen ab etwa sieben Optionen ohnehin diese Form.
 *
 * Gesucht wird in Titel **und** Zusatz, damit ein Projekt auch über den
 * Kundennamen auffindbar ist.
 *
 * Der Zustand ist bewusst zweigeteilt: solange niemand tippt, zeigt das Feld
 * das Label der Auswahl. Ein abgebrochener Suchversuch (Escape, Klick
 * daneben) lässt also keinen halben Text stehen, der so aussieht, als wäre
 * etwas anderes gewählt.
 */

export type ComboboxEintrag = {
  id: string;
  label: string;
  /** Zweite Zeile im Vorschlag und mitdurchsucht — z. B. der Kunde am Projekt. */
  zusatz?: string;
};

type Props = {
  /** Die gewählte ID; "" = nichts gewählt. */
  wert: string;
  eintraege: ComboboxEintrag[];
  onWaehlen: (id: string) => void;
  platzhalter?: string;
  /** Gesetzt = das Feld darf leer bleiben; steht als erster Eintrag in der Liste. */
  leerLabel?: string;
  ariaLabel?: string;
  /** Mehr Treffer bringen keine Übersicht, nur Scrollen. */
  maxTreffer?: number;
  disabled?: boolean;
};

export default function Combobox({
  wert, eintraege, onWaehlen, platzhalter, leerLabel, ariaLabel,
  maxTreffer = 40, disabled,
}: Props) {
  const gewaehlt = eintraege.find((eintrag) => eintrag.id === wert);
  const [such, setSuch] = useState("");
  const [tippt, setTippt] = useState(false);
  const [offen, setOffen] = useState(false);
  const [markiert, setMarkiert] = useState(-1);
  const box = useRef<HTMLDivElement>(null);

  const text = tippt ? such : gewaehlt?.label || "";

  const optionen = useMemo(() => {
    const suche = such.trim().toLowerCase();
    const gefiltert = tippt && suche
      ? eintraege.filter((eintrag) =>
        `${eintrag.label} ${eintrag.zusatz || ""}`.toLowerCase().includes(suche))
      : eintraege;
    const liste = gefiltert.slice(0, maxTreffer);
    return leerLabel ? [{ id: "", label: leerLabel } as ComboboxEintrag, ...liste] : liste;
  }, [eintraege, such, tippt, maxTreffer, leerLabel]);

  const schliessen = () => { setOffen(false); setTippt(false); setSuch(""); setMarkiert(-1); };

  useEffect(() => {
    if (!offen) return;
    const danebenGeklickt = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) schliessen();
    };
    document.addEventListener("mousedown", danebenGeklickt);
    return () => document.removeEventListener("mousedown", danebenGeklickt);
  }, [offen]);

  useEffect(() => setMarkiert(-1), [such]);

  const uebernehmen = (id: string) => { onWaehlen(id); schliessen(); };

  /**
   * Enter ohne Markierung übernimmt nur, wenn genau ein Treffer übrig ist —
   * sonst entschiede die Reihenfolge der Liste, und der Nutzer bekäme still
   * einen anderen Datensatz als den gemeinten.
   */
  const taste = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" && optionen.length) {
      event.preventDefault();
      setOffen(true);
      setMarkiert((stelle) => (stelle + 1) % optionen.length);
    } else if (event.key === "ArrowUp" && optionen.length) {
      event.preventDefault();
      setMarkiert((stelle) => (stelle <= 0 ? optionen.length - 1 : stelle - 1));
    } else if (event.key === "Enter") {
      if (!offen) return;
      event.preventDefault();
      if (markiert >= 0) uebernehmen(optionen[markiert].id);
      else if (optionen.length === 1) uebernehmen(optionen[0].id);
    } else if (event.key === "Escape") {
      if (!offen) return;
      event.preventDefault();
      schliessen();
    }
  };

  return (
    <div className="vg-combo" ref={box}>
      <div className="vg-combo-feld">
        <input
          value={text}
          disabled={disabled}
          onChange={(event) => { setSuch(event.target.value); setTippt(true); setOffen(true); }}
          onFocus={() => setOffen(true)}
          onKeyDown={taste}
          placeholder={platzhalter}
          aria-label={ariaLabel}
          autoComplete="off"
          role="combobox"
          aria-expanded={offen}
          aria-autocomplete="list"
        />
        {!disabled && (wert || tippt) && (
          <button
            type="button"
            className="vg-combo-leeren"
            onClick={() => { setSuch(""); setTippt(false); setMarkiert(-1); if (wert && leerLabel) onWaehlen(""); setOffen(true); }}
            aria-label="Eingabe leeren"
            title="Leeren"
          >
            ×
          </button>
        )}
      </div>

      {offen && !disabled && (
        <div className="vg-combo-liste" role="listbox">
          {optionen.length === 0 && (
            <p className="vg-combo-leermeldung">Nichts gefunden — anders schreiben oder weniger tippen.</p>
          )}
          {optionen.map((eintrag, stelle) => (
            <button
              type="button"
              key={eintrag.id || "__leer"}
              role="option"
              aria-selected={eintrag.id === wert}
              className={"vg-combo-eintrag"
                + (stelle === markiert ? " is-markiert" : "")
                + (eintrag.id === wert ? " is-gewaehlt" : "")
                + (eintrag.id ? "" : " ist-leerwahl")}
              onMouseEnter={() => setMarkiert(stelle)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => uebernehmen(eintrag.id)}
            >
              <strong>{eintrag.label}</strong>
              {eintrag.zusatz && <small>{eintrag.zusatz}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
